import { resolveLink } from './utils/scriptUtils';

/**
 * ViewerBridge — MCP-style interface between the AI agent tool layer and the viewer.
 *
 * Responsibilities:
 *  - Expose current viewer state as queryable methods:
 *      getScene()        — current scene id, name, description, link, connected rooms
 *      getPosition()     — current camera yaw / pitch
 *      getLanguage()     — active session language code
 *      getAudioStatus()  — { ai_speaking, audio_playing, idle }
 *      getTourProgress()   — { has_more, tour_complete, scenes_remaining, next_scene }
 *      isSceneLoaded()   — whether the current scene has finished loading
 *      isAnimating()     — whether a rotate animation is in progress
 *
 *  - Wrap async viewer commands as Promises that resolve only when the
 *    viewer confirms completion (scene:ready / rotate:ready events):
 *      navigateTo(room_id) — change scene, resolves on scene:ready (museum)
 *      rotate(opts)        — rotate view, resolves on rotate:ready (museum)
 *
 *  - Centralise all event-listener plumbing so ToolHandler stays thin
 */
class ViewerBridge {
  constructor(state, emitEvent, getTourGuide) {
    this.state = state;
    this.emitEvent = emitEvent;
    // lazy getter so the bridge can be created before tourGuide is ready
    this._getTourGuide = getTourGuide;
  }

  get tourGuide() { return this._getTourGuide(); }
  get isMuseum()  { return !!this.tourGuide?.isMuseum(); }

  // ─── State queries ───────────────────────────────────────────────

  getScene() {
    const room = this.state.getRoomInfo(this.state.currentScene);
    const scene = {
      id:          this.state.currentScene,
      name:        room?.title || this.state.currentScene,
      description: room?.description || '',
      link:        resolveLink(room?.link, this.state.language, this.state.tourData?.defaultLang || 'en') || '',
      connected:   (room?.hotSpots || []).filter(h => h.type === 'scene').map(h => h.sceneId),
    };
    console.log('[mcp] getScene', scene);
    return scene;
  }

  getPosition() {
    const pos = {
      yaw:   this.state.currentYaw   ?? null,
      pitch: this.state.currentPitch ?? null,
    };
    console.log('[mcp] getPosition', pos);
    return pos;
  }

  getLanguage() {
    const lang = this.state.language || this.tourGuide?.language || 'en';
    console.log('[mcp] getLanguage', lang);
    return lang;
  }

  /**
   * Returns the current audio status:
   *  - ai_speaking   — OpenAI Realtime voice stream is active
   *  - audio_playing — museum pre-recorded audio (MuseumAudioPlayer) is playing
   *  - idle          — neither is active
   */
  getAudioStatus() {
    const aiSpeaking    = !!this.tourGuide?._aiSpeaking;
    const audioPlaying  = !!this.tourGuide?.museumAudioPlayer?.isPlaying;
    const status = {
      ai_speaking:   aiSpeaking,
      audio_playing: audioPlaying,
      idle:          !aiSpeaking && !audioPlaying,
    };
    console.log('[mcp] getAudioStatus', status);
    return status;
  }

  /**
   * Check whether there are more guide scenes or points remaining.
   * Returns:
   *   has_more         — true if any scenes or points remain after the current position
   *   next_scene       — { id, name } of the next scene, or null
   *   scenes_remaining — count of scenes after the current one
   *   points_remaining — count of points in the current scene after the currently playing point
   *   tour_complete    — true only when on the last scene AND no more points remain
   */
  getTourProgress() {
    const guide   = (this.state.tourData?.guide || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
    const curId   = String(this.state.currentScene);
    const curIdx  = guide.findIndex(s => String(s.id) === curId);

    const scenesRemaining = curIdx === -1 ? guide.length : guide.length - curIdx - 1;
    const nextScene       = curIdx !== -1 && curIdx < guide.length - 1 ? guide[curIdx + 1] : null;

    // count remaining points in the current scene after the currently playing audio
    let pointsRemaining = 0;
    if (curIdx !== -1) {
      const currentGuideScene = guide[curIdx];
      const points = currentGuideScene?.points || [];
      const currentAudioUrl = this.tourGuide?._currentAudioUrl || null;
      if (currentAudioUrl && points.length) {
        const playingIdx = points.findIndex(p => {
          const link = p.link;
          if (!link) return false;
          const resolved = typeof link === 'object' ? (link[this.state.language] || Object.values(link)[0] || '') : link;
          return resolved === currentAudioUrl;
        });
        pointsRemaining = playingIdx !== -1 ? points.length - playingIdx - 1 : 0;
      }
    }

    const tourComplete = scenesRemaining === 0 && pointsRemaining === 0;

    const result = {
      has_more:         !tourComplete,
      tour_complete:    tourComplete,
      scenes_remaining: scenesRemaining,
      points_remaining: pointsRemaining,
      next_scene:       nextScene ? { id: nextScene.id, name: nextScene.name } : null,
    };
    console.log('[mcp] getTourProgress', result);
    return result;
  }

  isSceneLoaded() {
    const v = !!this.state._sceneReady;
    console.log('[mcp] isSceneLoaded', v);
    return v;
  }

  isAnimating() {
    const v = !!this.state._rotating;
    console.log('[mcp] isAnimating', v);
    return v;
  }

  // ─── Commands ────────────────────────────────────────────────────

  /**
   * Navigate to a room by R_ID or string key.
   * Resolves when the viewer signals scene:ready (museum) or immediately (default).
   */
  cancelNavigate() {
    if (this._navigateReadyHandler) {
      this.tourGuide.off('scene:ready', this._navigateReadyHandler);
      this._navigateReadyHandler = null;
    }
  }

  navigateTo(room_id, position) {
    // Cancel any prior in-flight navigate so its scene:ready listener
    // doesn't steal the event from the new one.
    this.cancelNavigate();

    return new Promise((resolve, reject) => {
      const room = this.state.getRoomInfo(room_id);
      const target = room || this.state.findBestRoomMatch(room_id);

      if (!target) {
        const available = (this.state.tourData?.scenes || []).map(s => `${s.id}`).join(', ');
        return reject(new Error(`Room "${room_id}" not found. Available: ${available}`));
      }

      const targetId = target.id ?? room_id;
      const result = {
        success:      true,
        current_room: targetId,
        message:      target.message || `Navigated to ${target.title || targetId}`,
      };

      // Already in the target scene — skip navigation, resolve immediately
      if (String(this.state.currentScene) === String(targetId)) {
        return resolve(result);
      }

      this.state.currentScene = targetId;
      this.emitEvent('on-change-scene', { room_id: targetId, position });

      if (this.isMuseum) {
        const onReady = () => {
          this.tourGuide.off('scene:ready', onReady); // self-remove so old handlers don't accumulate
          this._navigateReadyHandler = null;
          resolve(result);
        };
        this._navigateReadyHandler = onReady;
        this.tourGuide.on('scene:ready', onReady);
      } else {
        resolve(result);
      }
    });
  }

  /** Instantly position the camera without animation (fire-and-forget). */
  snapTo({ yaw, pitch }) {
    this.emitEvent('rotate-view', { yaw, pitch, hfov: 100, duration: 0 });
  }

  /**
   * Rotate the panoramic view.
   * Resolves when the viewer signals rotate:ready (museum) or immediately (default).
   */
  rotate({ yaw, pitch, hfov, duration }) {
    console.log('[mcp] rotate', { yaw, pitch, hfov, duration });
    return new Promise((resolve) => {
      this.emitEvent('rotate-view', { yaw, pitch, hfov, duration });
      const result = { success: true, yaw };

      if (this.isMuseum) {
        this.tourGuide.once('rotate:ready', () => {
          console.log('[mcp] rotate — rotate:ready received', yaw);
          resolve(result);
        });
      } else {
        resolve(result);
      }
    });
  }
  /**
   * Open an info-label hotspot by ID.
   * When waitForClose=true (default), resolves after the hotspot's media ends or the panel is closed.
   * When waitForClose=false (text type), resolves immediately so TTS can play while the label is open.
   */
  closeHotspot() {
    this.emitEvent('close-hotspot', {});
  }

  openHotspot(hotspotID, waitForClose = true) {
    console.log('[mcp] openHotspot', hotspotID, 'waitForClose:', waitForClose);
    return new Promise((resolve) => {
      this.emitEvent('open-hotspot', {
        hotspot_id:     hotspotID,
        scene_id:       this.state.currentScene,
        skip_navigate:  true,
        wait_for_close: waitForClose
      });
      if (!waitForClose) {
        resolve();
        return;
      }
      this.tourGuide.once('info:done', resolve);
    });
  }

  /**
   * Play a render zone by UUID.
   * Calls playZone directly on the RenderZoneRenderer (stored in state.renderZoneRenderer).
   * For video: resolves when video ends. For image: resolves immediately.
   */
  playRenderZone(renderZoneID, type = 'video') {
    return new Promise((resolve) => {
      this.emitEvent('play-render-zone', { render_zone_id: renderZoneID, type });
      if (type !== 'video') { resolve(); return; }
      this.tourGuide.once('render-zone:done', resolve);
    });
  }
}

export default ViewerBridge;
